import React, { useState, useEffect, useMemo } from 'react';
import './DailyInvoiceView.css';
import api from "../Service/api";

const DailyInvoiceView = () => {

  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getLocalDate)
  const [invoicesData, setInvoicesData] = useState(null);
  const [paymentsData, setPaymentsData] = useState(null);
  const [expensesData, setExpensesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  
  // Separate search/filter state for each tab
  const [invoicesSearch, setInvoicesSearch] = useState({ searchTerm: '', filterType: 'all' });
  const [paymentsSearch, setPaymentsSearch] = useState({ searchTerm: '', filterType: 'all' });
  const [expensesSearch, setExpensesSearch] = useState({ searchTerm: '' });

  const [expandedRows, setExpandedRows] = useState({});

const toggleRow = (key) => {
  setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
};

  // Calculate derived totals with return handling & factory use
  const derivedTotals = useMemo(() => {
    if (!invoicesData) return {
      grossCustomerSales: 0,
      grossVendorPurchases: 0,
      totalFactoryUse: 0,
      customerReturns: 0,
      vendorReturns: 0,
      netCustomerSales: 0,
      netVendorPurchases: 0,
      totalCustomerInvoices: 0,
      totalVendorInvoices: 0,
      totalFactoryUseInvoices: 0,
      totalCustomerReturnInvoices: 0,
      totalVendorReturnInvoices: 0
    };

    // Customer totals (Sales)
    const customerRegularInvoices = (invoicesData.customerInvoices || []).filter(inv => 
      inv.status?.toLowerCase() !== 'return'
    );
    const customerReturnInvoices = (invoicesData.customerInvoices || []).filter(inv => 
      inv.status?.toLowerCase() === 'return'
    );

    const grossCustomerSales = customerRegularInvoices.reduce((sum, inv) => 
      sum + (inv.grandTotal || 0), 0
    );
    const customerReturns = customerReturnInvoices.reduce((sum, inv) => 
      sum + Math.abs(inv.grandTotal || 0), 0
    );
    const netCustomerSales = grossCustomerSales - customerReturns;

    // Vendor totals (Purchases)
    const vendorRegularInvoices = (invoicesData.vendorInvoices || []).filter(inv => 
      inv.status?.toLowerCase() !== 'return'
    );
    const vendorReturnInvoices = (invoicesData.vendorInvoices || []).filter(inv => 
      inv.status?.toLowerCase() === 'return'
    );

    const grossVendorPurchases = vendorRegularInvoices.reduce((sum, inv) => 
      sum + (inv.grandTotal || 0), 0
    );
    const vendorReturns = vendorReturnInvoices.reduce((sum, inv) => 
      sum + Math.abs(inv.grandTotal || 0), 0
    );
    const netVendorPurchases = grossVendorPurchases - vendorReturns;

    // Factory Use totals
    const factoryUseInvoices = invoicesData.factoryUseInvoices || [];
    const totalFactoryUse = factoryUseInvoices.reduce((sum, inv) => 
      sum + (inv.grandTotal || 0), 0
    );

    return {
      grossCustomerSales,
      grossVendorPurchases,
      totalFactoryUse,
      customerReturns,
      vendorReturns,
      netCustomerSales,
      netVendorPurchases,
      totalCustomerInvoices: customerRegularInvoices.length,
      totalVendorInvoices: vendorRegularInvoices.length,
      totalFactoryUseInvoices: factoryUseInvoices.length,
      totalCustomerReturnInvoices: customerReturnInvoices.length,
      totalVendorReturnInvoices: vendorReturnInvoices.length
    };
  }, [invoicesData]);

  // Fetch invoices data
  const fetchDailyInvoices = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/daily-invoices/date/${selectedDate}`);
      setInvoicesData(response.data);
    } catch (err) {
      setError('Failed to fetch invoices. Please check your connection and try again.');
      console.error('Error fetching invoices:', err);
      setInvoicesData({
        date: selectedDate,
        customerInvoices: [],
        vendorInvoices: [],
        factoryUseInvoices: [],
        totalCustomerSales: 0,
        totalVendorPurchases: 0,
        totalFactoryUse: 0,
        totalInvoices: 0,
        totalGstAmount: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch payments data
  const fetchDailyPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/daily-invoices/api/daily-payments/date/${selectedDate}`);
      setPaymentsData(response.data);
    } catch (err) {
      setError('Failed to fetch payments. Please check your connection and try again.');
      console.error('Error fetching payments:', err);
      setPaymentsData({
        date: selectedDate,
        customerPayments: [],
        vendorPayments: [],
        totalCustomerPayments: 0,
        totalVendorPayments: 0,
        totalPayments: 0,
        netPaymentFlow: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch expenses data
  const fetchDailyExpenses = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/daily-invoices/api/daily-expenses/date/${selectedDate}`);
      setExpensesData(response.data);
    } catch (err) {
      setError('Failed to fetch expenses. Please check your connection and try again.');
      console.error('Error fetching expenses:', err);
      setExpensesData({
        date: selectedDate,
        dailyExpenses: [],
        totalDailyExpenses: 0,
        totalExpenses: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch today's data for current tab
  const fetchTodayData = async () => {
    try {
      setLoading(true);
      setError('');
      const today = new Date().toISOString().split('T')[0];
      setSelectedDate(today);
      
      if (activeTab === 'invoices') {
        await fetchDailyInvoices();
      } else if (activeTab === 'payments') {
        await fetchDailyPayments();
      } else if (activeTab === 'expenses') {
        await fetchDailyExpenses();
      }
      
    } catch (err) {
      setError('Failed to fetch today\'s data.');
      console.error('Error fetching today\'s data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when date changes or tab changes
  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchDailyInvoices();
    } else if (activeTab === 'payments') {
      fetchDailyPayments();
    } else if (activeTab === 'expenses') {
      fetchDailyExpenses();
    }
  }, [selectedDate, activeTab]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      amount = 0;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'PKR'
    }).format(amount);
  };

  const formatProductDetails = (product) => {
  const parts = [];

  if (product.size !== null && product.size !== undefined && product.size !== '') {
    parts.push(`Size: ${product.size}`);
  }
  if (product.ktae !== null && product.ktae !== undefined && product.ktae !== '') {
    parts.push(`Ktae: ${product.ktae}`);
  }
  if (product.gram !== null && product.gram !== undefined && product.gram !== '') {
    parts.push(`Gram: ${product.gram}`);
  }

  return parts.length > 0 ? parts.join(' • ') : 'N/A';
};

const handlePrintSection = (title, data) => {
  if (!data || data.length === 0) {
    alert(`No ${title.toLowerCase()} to print.`);
    return;
  }

  // Compute totals
  const totalAmount = data.reduce((sum, inv) => {
    const amt = inv.grandTotal || 0;
    return inv.isReturn ? sum - Math.abs(amt) : sum + amt;
  }, 0);
  const totalGst = data.reduce((sum, inv) => {
    const gst = inv.gstAmount || 0;
    return inv.isReturn ? sum - Math.abs(gst) : sum + gst;
  }, 0);
  const totalPaid = data.reduce((sum, inv) => {
    const paid = inv.amountPaid || 0;
    return inv.isReturn ? sum - Math.abs(paid) : sum + paid;
  }, 0);

  const formatAmt = (amount) => {
    const n = amount === null || amount === undefined || isNaN(amount) ? 0 : amount;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'PKR'
    }).format(n);
  };

  // Build the invoice rows + product rows
  const rowsHtml = data.map((invoice, idx) => {
    const isReturn = invoice.isReturn;
    const sign = isReturn ? '-' : '';
    const invoiceRow = `
      <tr class="${isReturn ? 'return-row' : ''}">
        <td>${invoice.invoiceNumber || 'N/A'}</td>
        <td>${invoice.customerOrVendorName || 'N/A'}</td>
        <td>${invoice.status || 'N/A'}</td>
        <td class="right">${sign}${formatAmt(Math.abs(invoice.grandTotal || 0))}</td>
        <td class="right">${sign}${formatAmt(Math.abs(invoice.gstAmount || 0))}</td>
        <td class="right">${sign}${formatAmt(Math.abs(invoice.amountPaid || 0))}</td>
      </tr>
    `;

    // Product rows (if any)
    let productRows = '';
    if (invoice.products && invoice.products.length > 0) {
      productRows = `
        <tr class="product-header-row">
          <td colspan="6">
            <table class="product-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Details</th>
                  <th class="right">Rate</th>
                  <th class="right">Qty</th>
                  <th class="right">Total</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.products.map(p => {
                  const details = formatProductDetails(p);
                  return `
                    <tr>
                      <td>${p.productName || 'N/A'}</td>
                      <td>${p.category || 'N/A'}</td>
                      <td>${details}</td>
                      <td class="right">${formatAmt(p.rate || 0)}</td>
                      <td class="right">${p.quantity ?? 0}</td>
                      <td class="right">${formatAmt(p.totalPrice || 0)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </td>
        </tr>
      `;
    }

    return invoiceRow + productRows;
  }).join('');

  const dateLabel = formatDate(invoicesData?.date || selectedDate);

  const printHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title} - ${dateLabel}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            color: #222;
          }
          h1 {
            font-size: 18px;
            margin: 0 0 4px 0;
          }
          h2 {
            font-size: 14px;
            margin: 0 0 16px 0;
            color: #555;
            font-weight: normal;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 12px;
          }
          th, td {
            border: 1px solid #999;
            padding: 6px 8px;
            font-size: 12px;
            text-align: left;
            vertical-align: top;
          }
          th {
            background: #eef4fb;
            font-weight: bold;
          }
          .right { text-align: right; }
          .return-row td {
            color: #b00;
          }
          .product-header-row > td {
            padding: 0;
            background: #fafafa;
          }
          .product-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }
          .product-table th, .product-table td {
            border: 1px solid #ccc;
            padding: 4px 6px;
            font-size: 11px;
          }
          .product-table th {
            background: #f0f0f0;
          }
          .totals {
            margin-top: 16px;
            font-size: 13px;
            text-align: right;
          }
          .totals div { margin: 4px 0; }
          .totals strong { display: inline-block; min-width: 120px; }
          @media print {
            body { padding: 8px; }
            h1 { font-size: 16px; }
          }
        </style>
      </head>
      <body>
        <h1>${title} Report</h1>
        <h2>Date: ${dateLabel}</h2>
        <table>
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Name</th>
              <th>Status</th>
              <th class="right">Amount</th>
              <th class="right">GST</th>
              <th class="right">Paid</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
          </tbody>
        </table>
        <div class="totals">
          <div><strong>Total Entries:</strong> ${data.length}</div>
          <div><strong>Net Amount:</strong> ${formatAmt(totalAmount)}</div>
          <div><strong>Net GST:</strong> ${formatAmt(totalGst)}</div>
          <div><strong>Total Paid:</strong> ${formatAmt(totalPaid)}</div>
        </div>
      </body>
    </html>
  `;

  // Open print window
  const printWindow = window.open('', '_blank', 'width=900,height=700');
  printWindow.document.open();
  printWindow.document.write(printHtml);
  printWindow.document.close();

  // Wait for content to render then print
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
  };
};
  const formatTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    
    if (dateTime instanceof Date) {
      return dateTime.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
    
    if (typeof dateTime === 'string') {
      if (dateTime.includes('T')) {
        const parsedDate = new Date(dateTime);
        if (!isNaN(parsedDate.getTime())) {
          return parsedDate.toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          });
        }
      }
      
      const parsedDate = new Date(dateTime);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
      
      if (dateTime.includes(':')) {
        const timeParts = dateTime.split(':');
        if (timeParts.length >= 2) {
          const hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);
          const period = hours >= 12 ? 'PM' : 'AM';
          const displayHours = hours % 12 || 12;
          return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
        }
        return dateTime;
      }
    }
    
    if (typeof dateTime === 'number') {
      const date = new Date(dateTime);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      }
    }
    
    return 'N/A';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'status-paid';
      case 'pending': return 'status-pending';
      case 'return': return 'status-return';
      case 'sale': return 'status-paid';
      case 'purchase': return 'status-pending';
      case 'factory_use': return 'status-factory';
      default: return 'status-default';
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  // Get current search state based on active tab
  const getCurrentSearchState = () => {
    switch (activeTab) {
      case 'invoices':
        return { ...invoicesSearch, setSearch: setInvoicesSearch };
      case 'payments':
        return { ...paymentsSearch, setSearch: setPaymentsSearch };
      case 'expenses':
        return { ...expensesSearch, setSearch: setExpensesSearch };
      default:
        return { searchTerm: '', filterType: 'all', setSearch: () => {} };
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const { value } = e.target;
    const currentState = getCurrentSearchState();
    
    if (activeTab === 'expenses') {
      currentState.setSearch({ searchTerm: value });
    } else {
      currentState.setSearch({ ...currentState, searchTerm: value });
    }
  };

  // Handle filter type change
  const handleFilterChange = (e) => {
    const { value } = e.target;
    const currentState = getCurrentSearchState();
    currentState.setSearch({ ...currentState, filterType: value });
  };

  // Handle clear search
  const handleClearSearch = () => {
    switch (activeTab) {
      case 'invoices':
        setInvoicesSearch({ searchTerm: '', filterType: 'all' });
        break;
      case 'payments':
        setPaymentsSearch({ searchTerm: '', filterType: 'all' });
        break;
      case 'expenses':
        setExpensesSearch({ searchTerm: '' });
        break;
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Refresh current tab data
  const refreshCurrentTab = () => {
    if (activeTab === 'invoices') {
      fetchDailyInvoices();
    } else if (activeTab === 'payments') {
      fetchDailyPayments();
    } else if (activeTab === 'expenses') {
      fetchDailyExpenses();
    }
  };

  // Calculate net cash flow (considering returns and factory use)
  const calculateNetCashFlow = () => {
    const netCustomerSales = derivedTotals.netCustomerSales;
    const netVendorPurchases = derivedTotals.netVendorPurchases;
    const totalFactoryUse = derivedTotals.totalFactoryUse;
    const customerPayments = paymentsData?.totalCustomerPayments || 0;
    const vendorPayments = paymentsData?.totalVendorPayments || 0;
    const dailyExpenses = expensesData?.totalDailyExpenses || 0;
    
    // Net cash flow = (Net Sales + Payments Received) - (Net Purchases + Payments Made + Expenses + Factory Use)
    return (netCustomerSales + customerPayments + totalFactoryUse) - (netVendorPurchases + vendorPayments + dailyExpenses);
  };

const renderInvoicesTable = () => {
  if (!invoicesData) return null;

  const { searchTerm, filterType } = invoicesSearch;

  // Helper: apply global search + type filter
  const applyFilters = (items) => {
    return items.filter(item => {
      if (filterType !== 'all') {
        if (filterType === 'customer' && !item.type.includes('Customer')) return false;
        if (filterType === 'vendor' && !item.type.includes('Vendor')) return false;
        if (filterType === 'factory' && !item.type.includes('Factory')) return false;
        if (filterType === 'sale' && !item.type.includes('Sale')) return false;
        if (filterType === 'purchase' && !item.type.includes('Purchase')) return false;
        if (filterType === 'return' && !item.type.includes('Return')) return false;
      }
      if (searchTerm) {
        const s = searchTerm.toLowerCase();
        return (
          item.invoiceNumber?.toString().toLowerCase().includes(s) ||
          item.customerOrVendorName?.toLowerCase().includes(s) ||
          item.status?.toLowerCase().includes(s) ||
          item.type?.toLowerCase().includes(s)
        );
      }
      return true;
    });
  };

  // Prepare data groups
  const customerSales = (invoicesData.customerInvoices || [])
    .filter(inv => inv.status?.toLowerCase() !== 'return')
    .map(inv => ({
      ...inv,
      type: 'Customer Sale',
      customerOrVendorName: inv.customerOrVendorName || inv.customerName,
      isReturn: false
    }));

  const customerReturns = (invoicesData.customerInvoices || [])
    .filter(inv => inv.status?.toLowerCase() === 'return')
    .map(inv => ({
      ...inv,
      type: 'Customer Return',
      customerOrVendorName: inv.customerOrVendorName || inv.customerName,
      isReturn: true
    }));

  const vendorPurchases = (invoicesData.vendorInvoices || [])
    .filter(inv => inv.status?.toLowerCase() !== 'return')
    .map(inv => ({
      ...inv,
      type: 'Vendor Purchase',
      customerOrVendorName: inv.customerOrVendorName || inv.vendorName,
      isReturn: false
    }));

  const vendorReturns = (invoicesData.vendorInvoices || [])
    .filter(inv => inv.status?.toLowerCase() === 'return')
    .map(inv => ({
      ...inv,
      type: 'Vendor Return',
      customerOrVendorName: inv.customerOrVendorName || inv.vendorName,
      isReturn: true
    }));

  const factoryUse = (invoicesData.factoryUseInvoices || []).map(inv => ({
    ...inv,
    type: 'Factory Use',
    customerOrVendorName: inv.customerOrVendorName || 'Factory',
    isReturn: false
  }));

  const filteredSales = applyFilters([...customerSales, ...customerReturns]);
  const filteredPurchases = applyFilters([...vendorPurchases, ...vendorReturns]);
  const filteredFactory = applyFilters(factoryUse);

  // Reusable table renderer with expandable product rows
  const renderTable = (title, data) => {
    if (data.length === 0) {
      return (
        <div key={title} className="sub-table-section">
          <h3>{title}</h3>
          <div className="no-data">No {title.toLowerCase()} found</div>
        </div>
      );
    }

    const totalAmount = data.reduce((sum, inv) => {
      const amt = inv.grandTotal || 0;
      return inv.isReturn ? sum - Math.abs(amt) : sum + amt;
    }, 0);
    const totalGst = data.reduce((sum, inv) => {
      const gst = inv.gstAmount || 0;
      return inv.isReturn ? sum - Math.abs(gst) : sum + gst;
    }, 0);
    const totalPaid = data.reduce((sum, inv) => {
      const paid = inv.amountPaid || 0;
      return inv.isReturn ? sum - Math.abs(paid) : sum + paid;
    }, 0);

    return (
      <div key={title} className="sub-table-section">
        <h3>
  {title} <span className="count-badge">{data.length}</span>
  <button
    className="print-section-btn"
    onClick={(e) => {
      e.stopPropagation();
      handlePrintSection(title, data);
    }}
    title={`Print ${title}`}
  >
    🖨️ Print
  </button>
</h3>
        <div className="table-responsive">
          <table className="daily-invoice-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>Invoice #</th>
                <th>Name</th>
                <th>Status</th>
                <th className="text-right">Amount</th>
                <th className="text-right">GST</th>
                <th className="text-right">Paid</th>
              </tr>
            </thead>
            <tbody>
              {data.map((invoice, idx) => {
                const rowKey = `${title}-${invoice.invoiceNumber || idx}`;
                const isExpanded = !!expandedRows[rowKey];
                const hasProducts = invoice.products && invoice.products.length > 0;

                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`${invoice.isReturn ? 'return-row' : ''} ${hasProducts ? 'expandable-row' : ''}`}
                      onClick={() => hasProducts && toggleRow(rowKey)}
                      style={{ cursor: hasProducts ? 'pointer' : 'default' }}
                    >
                      <td className="expand-cell">
                        {hasProducts && (isExpanded ? '▼' : '▶')}
                      </td>
                      <td className="invoice-number-cell">
                        <strong>{invoice.invoiceNumber || 'N/A'}</strong>
                        {invoice.isReturn && <span className="return-indicator">↩</span>}
                      </td>
                      <td>
                        <div className="name-cell">
                          <span className="type-icon">
                            {invoice.type.includes('Customer') ? '👤' :
                             invoice.type.includes('Vendor') ? '🏢' : '🏭'}
                          </span>
                          <span className="name-text">{invoice.customerOrVendorName || 'N/A'}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(invoice.status)}`}>
                          {invoice.status || 'N/A'}
                        </span>
                      </td>
                      <td className={`text-right amount ${invoice.isReturn ? 'negative' : ''}`}>
                        <strong>
                          {invoice.isReturn ? '-' : ''}
                          {formatCurrency(Math.abs(invoice.grandTotal || 0))}
                        </strong>
                      </td>
                      <td className="text-right gst-cell">
                        {invoice.isReturn ? '-' : ''}
                        {formatCurrency(Math.abs(invoice.gstAmount || 0))}
                      </td>
                      <td className="text-right paid-cell">
                        {invoice.isReturn ? '-' : ''}
                        {formatCurrency(Math.abs(invoice.amountPaid || 0))}
                      </td>
                    </tr>

                    {isExpanded && hasProducts && (
                      <tr className="product-details-row">
                        <td colSpan="7">
                          <table className="product-details-table">
                           <thead>
  <tr>
    <th>Product</th>
    <th>Category</th>
    <th>Details</th>
    <th className="text-right">Rate</th>
    <th className="text-right">Qty</th>
    <th className="text-right">Total</th>
  </tr>
</thead>
<tbody>
  {invoice.products.map((p, pIdx) => (
    <tr key={pIdx}>
      <td>{p.productName || 'N/A'}</td>
      <td>{p.category || 'N/A'}</td>
      <td className="details-cell">{formatProductDetails(p)}</td>
      <td className="text-right">
        {formatCurrency(p.rate || 0)}
      </td>
      <td className="text-right">{p.quantity ?? 0}</td>
      <td className="text-right">
        {formatCurrency(p.totalPrice || 0)}
      </td>
    </tr>
  ))}
</tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="table-footer">
          <div className="footer-left">{data.length} entries</div>
          <div className="footer-right">
            <div className="footer-total">Net Amount: {formatCurrency(totalAmount)}</div>
            <div className="footer-gst">Net GST: {formatCurrency(totalGst)}</div>
            <div className="footer-paid">Total Paid: {formatCurrency(totalPaid)}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="table-section">
      <div className="table-header-info">
        <span>Date: {formatDate(invoicesData?.date || selectedDate)}</span>
        <span>
          Showing {filteredSales.length + filteredPurchases.length + filteredFactory.length} filtered entries
        </span>
      </div>

      {renderTable('Sales', filteredSales)}
      {renderTable('Purchases', filteredPurchases)}
      {renderTable('Factory Use', filteredFactory)}
    </div>
  );
};
  // Render Payments Tab (unchanged)
  const renderPaymentsTable = () => {
    if (!paymentsData) return null;
    
    const { searchTerm, filterType } = paymentsSearch;
    
    const allPayments = [
      ...(paymentsData.customerPayments || []).map(payment => ({ 
        ...payment, 
        type: 'Customer Payment',
        payerName: payment.customerName || payment.payerName 
      })),
      ...(paymentsData.vendorPayments || []).map(payment => ({ 
        ...payment, 
        type: 'Vendor Payment',
        payerName: payment.vendorName || payment.payerName 
      }))
    ];

    const filteredPayments = allPayments.filter(payment => {
      if (filterType !== 'all') {
        if (filterType === 'customer' && !payment.type.includes('Customer')) return false;
        if (filterType === 'vendor' && !payment.type.includes('Vendor')) return false;
      }
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (payment.invoiceNumber && payment.invoiceNumber.toString().toLowerCase().includes(searchLower)) ||
          (payment.payerName && payment.payerName.toLowerCase().includes(searchLower)) ||
          (payment.billingMonth && payment.billingMonth.toLowerCase().includes(searchLower))
        );
      }
      
      return true;
    });

    return (
      <div className="table-section">
        <div className="table-header-info">
          <span>Date: {formatDate(paymentsData?.date || selectedDate)}</span>
          <span>Showing {filteredPayments.length} payments</span>
        </div>
        
        <div className="table-responsive">
          <table className="daily-invoice-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Payer/Receiver</th>
                <th>Type</th>
                <th>Billing Month</th>
                <th>Payment Time</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <tr key={`payment-${payment.id || index}`}>
                  <td className="invoice-number-cell">
                    <strong>{payment.invoice || 'N/A'}</strong>
                  </td>
                  <td>
                    <div className="name-cell">
                      <span className="type-icon">
                        {payment.type.includes('Customer') ? '👤' : '🏢'}
                      </span>
                      <span className="name-text">{payment.payerName || 'N/A'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`type-badge ${payment.type.includes('Customer') ? 'customer' : 'vendor'}`}>
                      {payment.type}
                    </span>
                  </td>
                  <td className="billing-month-cell">{payment.billingMonth || 'N/A'}</td>
                  <td className="time-cell">{formatTime(payment.paymentTime)}</td>
                  <td className="text-right amount">
                    <strong>{formatCurrency(payment.amountPaid !== null && payment.amountPaid !== undefined ? payment.amountPaid : 0)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="table-footer">
          <div className="footer-left">
            Total: {filteredPayments.length} payments
          </div>
          <div className="footer-right">
            <div className="footer-total">
              Total Received: {formatCurrency(
                (paymentsData.customerPayments || []).reduce((sum, p) => 
                  sum + (p.amountPaid !== null && p.amountPaid !== undefined ? p.amountPaid : 0), 0)
              )}
            </div>
            <div className="footer-total vendor">
              Total Paid: {formatCurrency(
                (paymentsData.vendorPayments || []).reduce((sum, p) => 
                  sum + (p.amountPaid !== null && p.amountPaid !== undefined ? p.amountPaid : 0), 0)
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Expenses Tab (unchanged)
  const renderExpensesTable = () => {
    if (!expensesData) return null;
    
    const { searchTerm } = expensesSearch;
    
    const filteredExpenses = (expensesData.dailyExpenses || []).filter(expense => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (expense.category && expense.category.toLowerCase().includes(searchLower)) ||
          (expense.description && expense.description.toLowerCase().includes(searchLower))
        );
      }
      return true;
    });

    return (
      <div className="table-section">
        <div className="table-header-info">
          <span>Date: {formatDate(expensesData?.date || selectedDate)}</span>
          <span>Showing {filteredExpenses.length} expenses</span>
        </div>
        
        <div className="table-responsive">
          <table className="daily-invoice-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Description</th>
                <th>Type</th>
                <th>Time</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map((expense, index) => (
                <tr key={`expense-${expense.id || index}`}>
                  <td className="category-cell">
                    <strong>{expense.category || 'N/A'}</strong>
                  </td>
                  <td className="description-cell">{expense.description || 'N/A'}</td>
                  <td>
                    <span className={`type-badge ${expense.expenseType?.toLowerCase() || 'daily'}`}>
                      {expense.expenseType || 'DAILY'}
                    </span>
                  </td>
                  <td className="time-cell">{expense.expenseTime || 'N/A'}</td>
                  <td className="text-right amount">
                    <strong>{formatCurrency(expense.amount !== null && expense.amount !== undefined ? expense.amount : 0)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="table-footer">
          <div className="footer-left">
            Total: {filteredExpenses.length} expenses
          </div>
          <div className="footer-right">
            <div className="footer-total">
              Total Expenses: {formatCurrency(
                filteredExpenses.reduce((sum, exp) => 
                  sum + (exp.amount !== null && exp.amount !== undefined ? exp.amount : 0), 0)
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => {
    const currentSearchState = getCurrentSearchState();
    const hasSearchTerm = currentSearchState.searchTerm || 
      (activeTab !== 'expenses' && currentSearchState.filterType !== 'all');
    
    const emptyMessages = {
      invoices: hasSearchTerm 
        ? 'No invoices match your search criteria' 
        : 'No invoices found for this date',
      payments: hasSearchTerm
        ? 'No payments match your search criteria'
        : 'No payments found for this date',
      expenses: currentSearchState.searchTerm
        ? 'No expenses match your search criteria'
        : 'No expenses found for this date'
    };

    return (
      <div className="no-data">
        <p>{emptyMessages[activeTab]}</p>
      </div>
    );
  };

  const renderActiveTable = () => {
    const hasData = {
      invoices: invoicesData && ((invoicesData.customerInvoices?.length || 0) + (invoicesData.vendorInvoices?.length || 0) + (invoicesData.factoryUseInvoices?.length || 0) > 0),
      payments: paymentsData && ((paymentsData.customerPayments?.length || 0) + (paymentsData.vendorPayments?.length || 0) > 0),
      expenses: expensesData && (expensesData.dailyExpenses?.length || 0) > 0
    };

    switch (activeTab) {
      case 'invoices':
        return hasData.invoices ? renderInvoicesTable() : renderEmptyState();
      case 'payments':
        return hasData.payments ? renderPaymentsTable() : renderEmptyState();
      case 'expenses':
        return hasData.expenses ? renderExpensesTable() : renderEmptyState();
      default:
        return renderEmptyState();
    }
  };

  // Get current values for the input fields
  const currentSearchState = getCurrentSearchState();
  const showClearButton = activeTab === 'expenses' 
    ? currentSearchState.searchTerm 
    : currentSearchState.searchTerm || currentSearchState.filterType !== 'all';

  return (
    <div className="daily-invoice-container">
      {/* Header */}
      <div className="invoice-header">
        <div className="header-left">
          <h1>📊 Daily Financial Report</h1>
          <p className="subtitle">View invoices, payments, and expenses for a specific date</p>
        </div>
        <div className="header-right">
          <input 
            type="date" 
            value={selectedDate}
            onChange={handleDateChange}
            className="date-picker"
            style={{ color: '#000000', backgroundColor: '#ffffff' }}
          />
          <button 
            onClick={fetchTodayData}
            className="today-btn"
            disabled={loading}
          >
            Today
          </button>
          <button 
            onClick={refreshCurrentTab}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? 'Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Summary Cards - Updated with factory use */}
      <div className="summary-cards">
        <div className="summary-card total-invoices">
          <div className="card-label">Total Invoices</div>
          <div className="card-value">{invoicesData?.totalInvoices || 0}</div>
          <div className="card-subtitle">
            Sales: {derivedTotals.totalCustomerInvoices} | 
            Purchases: {derivedTotals.totalVendorInvoices} | 
            Factory: {derivedTotals.totalFactoryUseInvoices} |
            Returns: {derivedTotals.totalCustomerReturnInvoices + derivedTotals.totalVendorReturnInvoices}
          </div>
        </div>
        
        <div className="summary-card customer">
          <div className="card-label">Customer Sales (Net)</div>
          <div className="card-value">{formatCurrency(derivedTotals.netCustomerSales)}</div>
          <div className="card-subtitle">
            Gross: {formatCurrency(derivedTotals.grossCustomerSales)} | 
            Returns: {formatCurrency(derivedTotals.customerReturns)}
          </div>
        </div>
        
        <div className="summary-card vendor">
          <div className="card-label">Vendor Purchases (Net)</div>
          <div className="card-value">{formatCurrency(derivedTotals.netVendorPurchases)}</div>
          <div className="card-subtitle">
            Gross: {formatCurrency(derivedTotals.grossVendorPurchases)} | 
            Returns: {formatCurrency(derivedTotals.vendorReturns)}
          </div>
        </div>

        {/* New: Factory Use card */}
        <div className="summary-card factory">
          <div className="card-label">Factory Use</div>
          <div className="card-value">{formatCurrency(derivedTotals.totalFactoryUse)}</div>
          <div className="card-subtitle">
            Internal consumption
          </div>
        </div>
        
        <div className="summary-card gst">
          <div className="card-label">Total GST (Net)</div>
          <div className="card-value">{formatCurrency(invoicesData?.totalGstAmount || 0)}</div>
          <div className="card-subtitle">
            Sales GST: {formatCurrency(derivedTotals.netCustomerSales * 0.18)} |
            Purchase GST: {formatCurrency(derivedTotals.netVendorPurchases * 0.18)}
          </div>
        </div>
        
        <div className="summary-card payment-in">
          <div className="card-label">Payments Received</div>
          <div className="card-value">{formatCurrency(paymentsData?.totalCustomerPayments || 0)}</div>
          <div className="card-subtitle">
            From customers
          </div>
        </div>
        
        <div className="summary-card payment-out">
          <div className="card-label">Payments Made</div>
          <div className="card-value">{formatCurrency(paymentsData?.totalVendorPayments || 0)}</div>
          <div className="card-subtitle">
            To vendors
          </div>
        </div>
        
        <div className="summary-card expense">
          <div className="card-label">Daily Expenses</div>
          <div className="card-value">{formatCurrency(expensesData?.totalDailyExpenses || 0)}</div>
          <div className="card-subtitle">
            Total expenses
          </div>
        </div>
        
        <div className="summary-card net">
          <div className="card-label">Net Cash Flow</div>
          <div className="card-value">{formatCurrency(calculateNetCashFlow())}</div>
          <div className="card-subtitle">
            (Sales + Received + Factory Use) - (Purchases + Paid + Expenses)
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => handleTabChange('invoices')}
          >
            📋 Invoices ({invoicesData ? (invoicesData.customerInvoices?.length || 0) + (invoicesData.vendorInvoices?.length || 0) + (invoicesData.factoryUseInvoices?.length || 0) : 0})
          </button>
          <button 
            className={`tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => handleTabChange('payments')}
          >
            💰 Payments ({paymentsData ? (paymentsData.customerPayments?.length || 0) + (paymentsData.vendorPayments?.length || 0) : 0})
          </button>
          <button 
            className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => handleTabChange('expenses')}
          >
            📝 Expenses ({expensesData ? (expensesData.dailyExpenses?.length || 0) : 0})
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder={activeTab === 'invoices' ? "🔍 Search invoices..." : 
                        activeTab === 'payments' ? "🔍 Search payments..." : 
                        "🔍 Search expenses..."}
            value={currentSearchState.searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
        
        {activeTab !== 'expenses' && (
          <div className="type-filter">
            <select 
              value={currentSearchState.filterType} 
              onChange={handleFilterChange}
              className="type-select"
            >
              <option value="all">All Types</option>
              <option value="customer">Customer</option>
              <option value="vendor">Vendor</option>
              <option value="factory">Factory Use</option>
              {activeTab === 'invoices' && (
                <>
                  <option value="sale">Sales Only</option>
                  <option value="purchase">Purchases Only</option>
                  <option value="return">Returns Only</option>
                </>
              )}
            </select>
          </div>
        )}
        
        <div className="action-buttons">
          {showClearButton && (
            <button 
              onClick={handleClearSearch}
              className="clear-btn"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-alert">
          ⚠️ {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading {activeTab}...</p>
        </div>
      ) : (
        <div className="invoice-table-container">
          {renderActiveTable()}
        </div>
      )}
    </div>
  );
};

export default DailyInvoiceView;